import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Button, Typography, Stack, TextField, Box, CircularProgress, Paper, useTheme, alpha,
} from '@mui/material';
import { CheckCircleOutline, History, ErrorOutline, FamilyRestroomOutlined } from '@mui/icons-material';
import { useSnackbar } from 'notistack';

import { admissionApplicationService, StaffCreatePayload } from '../../../services/admissions/admissionApplicationService';
import { peopleService, type GuardianLookup } from '../../../services/peopleService';
import { AdmissionCycle, AdmissionFormSection, IdNamePair } from '../../../types/admissions';
import DynamicFieldRenderer from '../form/DynamicFieldRenderer';
import DocumentChecklistInput, { documentFileProblem } from '../form/DocumentChecklistInput';
import {
  activeFormSections, splitFormValues, validateDynamicFields, fieldDisplayValue, withOptionalPhoto, ON_FILE_FAMILY_FIELDS,
} from '../form/admissionFormFields';
import { personPhotoService } from '../../../services/personPhotoService';
import { cnicDigits, formatCnic } from '../../../utils/cnic';
import { useAuth } from '../../../context/AuthContext';
import { formatMoneyFull } from '../../reports/finance/chartConfig';
import StepDialog from '../../common/StepDialog';
import { useDobHint } from '../form/useDobHint';
import { pathwayFor } from '../../../utils/admissionAssessment';
import { grLabel } from '../../../utils/personRef';
import { classesTakingApplications } from '../../../utils/admissionClasses';
import { isEmailAddress } from '../../../utils/emailAddress';
import { admissionInviteService, type AdmissionInvite } from '../../../services/admissions/admissionInviteService';
import { FormLinkFields, FormLinkSent, OpenInviteNotice } from './FormLinkParts';

/**
 * Staff-created application (Journey 2). Three steps:
 *
 *   1. Identify — CNIC-first. The school's records are checked as soon as 13
 *      digits are entered; a match switches to the re-admission path (GR
 *      number kept, family/guardian fields skipped). For a new applicant the
 *      father's CNIC is checked too: a father already at the school (a
 *      sibling's parent) brings his family — the child joins it, and the
 *      parents' fields arrive pre-filled from the record. Anything corrected
 *      here is saved back to the family's guardian records.
 *   2. Details — the cycle's own form (`cycle.formTemplate`, same snapshot
 *      the public form uses). System-required fields are always required;
 *      any other field the admin marked required in the form settings is
 *      required too.
 *   3. Review — every answer, grouped as on the form, before submit.
 *
 * Or, once the applicant is identified, Send form link: the family gets
 * their own link and fills steps 2–3 in at home (FormLinkParts). A link
 * already out for the applicant is shown on step 1, to copy or send again.
 */

type Mode = 'fresh' | 're_admit';

type Lookup =
  | { state: 'idle' }
  | { state: 'checking' }
  | { state: 'error'; message: string }
  | { state: 'new'; cnic: string }
  /** On file already — a former student (with a GR number) or an earlier applicant. */
  | { state: 'returning'; cnic: string; grNumber?: string; personId: string; name: string };

type FatherLookup =
  | { state: 'idle' }
  | { state: 'checking' }
  | { state: 'error'; message: string }
  | { state: 'new' }
  | { state: 'found'; match: GuardianLookup };

interface Props {
  open: boolean;
  cycle: AdmissionCycle;
  onCancel: () => void;
  onCreated: (applicationId: string, applicationNumber: string) => Promise<void> | void;
  /** A form link was sent — the cycle's Form links list changed. */
  onInvited: () => void;
}

const STEPS = ['Identify', 'Details', 'Review'];
const LINK_STEPS = ['Identify', 'Form link'];

const FATHER_KEYS = ['fatherName', 'fatherPhone', 'fatherCnic', 'fatherEmail', 'fatherOccupation'];
const MOTHER_KEYS = ['motherName', 'motherPhone', 'motherCnic', 'motherEmail', 'motherOccupation'];

const trimOrUndef = (v: unknown): string | undefined => {
  if (v === undefined || v === null) return undefined;
  const s = String(v).trim();
  return s === '' ? undefined : s;
};

const WalkInApplicationDialog: React.FC<Props> = ({ open, cycle, onCancel, onCreated, onInvited }) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const { currency } = useAuth();
  const [stepIdx, setStepIdx] = useState(0);
  const [cnic, setCnic] = useState('');
  const [lookup, setLookup] = useState<Lookup>({ state: 'idle' });
  const [fatherCnic, setFatherCnic] = useState('');
  const [fatherLookup, setFatherLookup] = useState<FatherLookup>({ state: 'idle' });
  const [values, setValues] = useState<Record<string, any>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [submitting, setSubmitting] = useState(false);
  // Documents handed in at the desk (the cycle's checklist). Optional here —
  // they can also be added later on the Documents tab; enrollment needs the
  // required ones verified either way.
  const [docFiles, setDocFiles] = useState<Record<string, File | null>>({});
  // Send form link instead of filling the form in now.
  const [path, setPath] = useState<'fill' | 'link'>('fill');
  const [linkEmail, setLinkEmail] = useState('');
  const [linkClassId, setLinkClassId] = useState('');
  const [sent, setSent] = useState<(AdmissionInvite & { emailed: boolean }) | null>(null);
  // A link already out for this applicant, and whether form links are emailed at all.
  const [openInvite, setOpenInvite] = useState<AdmissionInvite | null>(null);
  const [emailOn, setEmailOn] = useState(false);
  const detailsRef = useRef<HTMLDivElement>(null);
  const cnicInputRef = useRef<HTMLInputElement>(null);

  const mode: Mode = lookup.state === 'returning' ? 're_admit' : 'fresh';

  const classOptions = useMemo(
    () => (cycle.allowedClassIds || []).filter((c): c is IdNamePair => typeof c === 'object'),
    [cycle.allowedClassIds],
  );
  // Only classes taking applications can be chosen (a full class may be stopped).
  const openClassOptions = useMemo(() => classesTakingApplications(cycle), [cycle]);
  const dobHint = useDobHint(cycle.eligibility, classOptions, values.classId, values.dob);

  // The family the child joins when the father is already a parent here.
  const joining = fatherLookup.state === 'found' ? fatherLookup.match : null;

  const motherOnRecord = joining?.guardians.find(g => g.relation === 'mother') ?? null;

  // The cycle's form minus what this flow doesn't ask: the CNICs (step 1;
  // a mother already on record keeps hers — CNIC is how a parent is known)
  // and, for a returning student, the family fields already on file. The
  // desk never has to have a photo, even when the online form requires one.
  const sections: AdmissionFormSection[] = useMemo(() => {
    const hidden = new Set<string>(['cnic', 'fatherCnic']);
    if (mode === 're_admit') ON_FILE_FAMILY_FIELDS.forEach(k => hidden.add(k));
    if (motherOnRecord) hidden.add('motherCnic');
    return withOptionalPhoto(activeFormSections(cycle.formTemplate?.sections, hidden));
  }, [cycle.formTemplate?.sections, mode, motherOnRecord]);

  // A sibling's parents arrive pre-filled from the family record, still
  // editable — what staff correct is saved back to the record. Switching to
  // a different father clears what came from the previous one.
  useEffect(() => {
    const father = joining?.guardians.find(g => g.relation === 'father');
    setValues(prev => {
      const next = { ...prev };
      for (const k of [...FATHER_KEYS, ...MOTHER_KEYS]) delete next[k];
      if (!joining) return next;
      const fill = (prefix: 'father' | 'mother', g?: { name: string; phone: string; email?: string; occupation?: string } | null) => {
        if (!g) return;
        next[`${prefix}Name`] = g.name;
        next[`${prefix}Phone`] = g.phone;
        if (g.email) next[`${prefix}Email`] = g.email;
        if (g.occupation) next[`${prefix}Occupation`] = g.occupation;
      };
      fill('father', father);
      fill('mother', motherOnRecord);
      return next;
    });
  }, [joining, motherOnRecord]);

  const errors = useMemo(() => validateDynamicFields(values, sections), [values, sections]);

  // The chosen class's documents (its pathway's checklist).
  const checklist = useMemo(() => pathwayFor(cycle, values.classId).documentChecklist || [], [cycle, values.classId]);
  const docErrors = useMemo(() => {
    const out: Record<string, string> = {};
    for (const item of checklist) {
      const file = docFiles[item.key];
      const problem = file ? documentFileProblem(file, item) : null;
      if (problem) out[item.key] = problem;
    }
    return out;
  }, [checklist, docFiles]);

  // ─── Step 1: check the school's records as soon as the CNIC is complete ──
  useEffect(() => {
    if (cnic.length !== 13) { setLookup({ state: 'idle' }); return; }
    let cancelled = false;
    setLookup({ state: 'checking' });
    peopleService.listPeople({ q: cnic, limit: 1 })
      .then((res) => {
        if (cancelled) return;
        const match = res.items?.[0];
        setLookup(match && match.cnic === cnic
          ? { state: 'returning', cnic, grNumber: match.grNumber, personId: match._id, name: match.name }
          : { state: 'new', cnic });
      })
      .catch((err: any) => {
        if (!cancelled) setLookup({ state: 'error', message: err?.message || 'Could not check records' });
      });
    return () => { cancelled = true; };
  }, [cnic]);

  // ─── Step 1: is a form link already out for this applicant? ───────────────
  useEffect(() => {
    setOpenInvite(null);
    if (cnic.length !== 13) return undefined;
    let cancelled = false;
    admissionInviteService.list(cycle._id, { cnic, limit: 1 })
      .then(res => { if (!cancelled) { setOpenInvite(res.items[0] ?? null); setEmailOn(res.emailOn); } })
      .catch(() => { /* the desk carries on; creating a link reports a duplicate itself */ });
    return () => { cancelled = true; };
  }, [cnic, cycle._id]);

  // ─── Step 1: is the father already a parent at the school? ───────────────
  useEffect(() => {
    if (lookup.state !== 'new' || fatherCnic.length !== 13) { setFatherLookup({ state: 'idle' }); return; }
    if (fatherCnic === cnic) { setFatherLookup({ state: 'error', message: 'The father\'s CNIC can\'t be the same as the applicant\'s.' }); return; }
    let cancelled = false;
    setFatherLookup({ state: 'checking' });
    peopleService.lookupGuardian(fatherCnic)
      .then(match => { if (!cancelled) setFatherLookup(match ? { state: 'found', match } : { state: 'new' }); })
      .catch((err: any) => { if (!cancelled) setFatherLookup({ state: 'error', message: err?.message || 'Could not check records' }); });
    return () => { cancelled = true; };
  }, [fatherCnic, cnic, lookup.state]);

  const identified = lookup.state === 'returning'
    || (lookup.state === 'new' && (fatherLookup.state === 'new' || fatherLookup.state === 'found'));

  const reset = () => {
    setStepIdx(0);
    setCnic('');
    setLookup({ state: 'idle' });
    setFatherCnic('');
    setFatherLookup({ state: 'idle' });
    setValues({});
    setTouched({});
    setDocFiles({});
    setPath('fill');
    setLinkEmail('');
    setLinkClassId('');
    setSent(null);
    setOpenInvite(null);
  };

  const close = () => {
    if (submitting) return;
    onCancel();
    setTimeout(reset, 150);
  };

  const goToDetails = () => {
    setStepIdx(1);
  };

  const continueFromDetails = () => {
    const errorKeys = Object.keys(errors);
    const docProblem = Object.values(docErrors)[0];
    if (errorKeys.length === 0 && docProblem) { enqueueSnackbar(docProblem, { variant: 'warning' }); return; }
    if (errorKeys.length === 0) { setStepIdx(2); return; }
    const all: Record<string, boolean> = {};
    for (const s of sections) for (const f of s.fields) all[f.key] = true;
    setTouched(all);
    // Bring the first incomplete field into view, in form order.
    const firstKey = sections.flatMap(s => s.fields.map(f => f.key)).find(k => errors[k]);
    detailsRef.current
      ?.querySelector(`[data-field-key="${firstKey}"]`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    enqueueSnackbar(
      `${errorKeys.length} field${errorKeys.length === 1 ? ' needs' : 's need'} attention`,
      { variant: 'warning' },
    );
  };

  const sendLink = async () => {
    setSubmitting(true);
    try {
      const res = await admissionInviteService.create({
        cycleId: cycle._id,
        cnic,
        ...(lookup.state === 'returning' ? { personId: lookup.personId } : { fatherCnic }),
        email: linkEmail.trim() || undefined,
        classId: linkClassId || undefined,
      });
      setSent(res);
      onInvited();
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Couldn’t create the form link', { variant: 'error' });
      // Someone else sent one meanwhile: show it on step 1.
      if (err?.details?.inviteId) {
        const res = await admissionInviteService.list(cycle._id, { cnic, limit: 1 });
        setOpenInvite(res.items[0] ?? null);
        setPath('fill');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const { topLevel, formData } = splitFormValues(values, { sections });
      const payload: StaffCreatePayload = {
        cnic,
        name: (topLevel.name as string)?.trim(),
        fatherName: (topLevel.fatherName as string)?.trim() || '',
        fatherPhone: (topLevel.fatherPhone as string)?.trim() || '',
        address: (topLevel.address as string)?.trim(),
        gender: topLevel.gender as 'male' | 'female' | 'other',
        classId: topLevel.classId as string,
        candidateContact: trimOrUndef(topLevel.candidateContact),
        email: trimOrUndef(topLevel.email),
        dob: topLevel.dob ? String(topLevel.dob) : undefined,
        previousSchool: trimOrUndef(topLevel.previousSchool),
        previousPercentage: topLevel.previousPercentage !== undefined ? Number(topLevel.previousPercentage) : undefined,
        fatherCnic: lookup.state === 'new' ? fatherCnic : trimOrUndef(topLevel.fatherCnic),
        fatherEmail: trimOrUndef(topLevel.fatherEmail),
        fatherOccupation: trimOrUndef(topLevel.fatherOccupation),
        motherName: trimOrUndef(topLevel.motherName),
        motherPhone: trimOrUndef(topLevel.motherPhone),
        motherCnic: motherOnRecord ? motherOnRecord.cnic : trimOrUndef(topLevel.motherCnic),
        motherEmail: trimOrUndef(topLevel.motherEmail),
        motherOccupation: trimOrUndef(topLevel.motherOccupation),
        formData: Object.keys(formData).length > 0 ? formData : undefined,
      };
      if (lookup.state === 'returning') payload.personId = lookup.personId;
      // The child joins the father's family; the parents' details as
      // confirmed here are saved to their records.
      if (joining) payload.familyId = joining.family._id;

      const res = await admissionApplicationService.createStaffApplication(cycle._id, payload);
      // The application exists now; a failed document upload is reported but
      // doesn't undo it — staff can add it from the Documents tab.
      const failed: string[] = [];
      for (const item of checklist) {
        const file = docFiles[item.key];
        if (!file) continue;
        try {
          await admissionApplicationService.uploadDocument(res.applicationId, item.key, file);
        } catch {
          failed.push(item.label);
        }
      }
      if (values.photo instanceof File) {
        try {
          await personPhotoService.upload({ application: res.applicationId }, values.photo);
        } catch {
          failed.push('the photo');
        }
      }
      if (failed.length) {
        enqueueSnackbar(`Application ${res.applicationNumber} created, but ${failed.join(', ')} didn't upload — add it from the applicant's drawer.`, { variant: 'warning' });
      } else {
        enqueueSnackbar(`Application ${res.applicationNumber} created`, { variant: 'success' });
      }
      close();
      await onCreated(res.applicationId, res.applicationNumber);
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Failed to create application', { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const fee = cycle.applicationFeeAmount > 0 ? formatMoneyFull(cycle.applicationFeeAmount, currency) : null;

  // ─── Render ────────────────────────────────────────────────────────────
  const footer = (
    <>
      {!sent && (
        <Button onClick={close} disabled={submitting} sx={{ textTransform: 'none', color: 'text.secondary' }}>
          Cancel
        </Button>
      )}
      <Box sx={{ flex: 1 }} />
      {path === 'link' ? (sent ? (
        <Button variant="contained" disableElevation onClick={close} sx={{ textTransform: 'none' }}>Done</Button>
      ) : (
        <>
          <Button
            variant="outlined"
            onClick={() => setPath('fill')}
            disabled={submitting}
            sx={{ textTransform: 'none', borderColor: 'divider', color: 'text.secondary' }}
          >
            Back
          </Button>
          <Button
            variant="contained"
            disableElevation
            onClick={sendLink}
            disabled={submitting || (!!linkEmail.trim() && !isEmailAddress(linkEmail))}
            sx={{ textTransform: 'none' }}
          >
            {submitting ? 'Sending…' : emailOn && linkEmail.trim() ? 'Send link' : 'Create link'}
          </Button>
        </>
      )) : null}
      {path === 'fill' && stepIdx > 0 && (
        <Button
          variant="outlined"
          onClick={() => setStepIdx(stepIdx - 1)}
          disabled={submitting}
          sx={{ textTransform: 'none', borderColor: 'divider', color: 'text.secondary' }}
        >
          Back
        </Button>
      )}
      {path === 'fill' && stepIdx === 0 && !openInvite && (
        <Button variant="outlined" onClick={() => setPath('link')} disabled={!identified} sx={{ textTransform: 'none' }}>
          Send form link
        </Button>
      )}
      {path === 'fill' && stepIdx === 0 && (
        <Button variant="contained" disableElevation onClick={goToDetails} disabled={!identified} sx={{ textTransform: 'none' }}>
          Fill in now
        </Button>
      )}
      {path === 'fill' && stepIdx === 1 && (
        <Button variant="contained" disableElevation onClick={continueFromDetails} sx={{ textTransform: 'none' }}>
          Review
        </Button>
      )}
      {path === 'fill' && stepIdx === 2 && (
        <Button variant="contained" disableElevation onClick={submit} disabled={submitting} sx={{ textTransform: 'none' }}>
          {submitting ? 'Creating…' : 'Create application'}
        </Button>
      )}
    </>
  );

  return (
    <StepDialog
      open={open}
      onClose={close}
      closeDisabled={submitting}
      title="New walk-in application"
      subtitle={`${cycle.name}${fee ? `  ·  Application fee ${fee}` : ''}`}
      steps={path === 'link' ? LINK_STEPS : STEPS}
      activeStep={path === 'link' ? (sent ? 2 : 1) : stepIdx}
      // Fixed height so the dialog doesn't resize between steps.
      height={{ sm: 'min(760px, 92vh)' }}
      footer={footer}
      bodyRef={detailsRef}
      // The dialog's focus trap claims focus on open; hand it to the CNIC
      // field once the opening transition finishes so staff can type at once.
      dialogProps={{ TransitionProps: { onEntered: () => cnicInputRef.current?.focus() } }}
    >
        {path === 'link' && (sent ? <FormLinkSent invite={sent} /> : (
          <FormLinkFields
            who={lookup.state === 'returning' ? lookup.name : 'The applicant'}
            email={linkEmail}
            classId={linkClassId}
            emailOn={emailOn}
            classOptions={openClassOptions}
            disabled={submitting}
            onEmail={setLinkEmail}
            onClass={setLinkClassId}
          />
        ))}

        {path === 'fill' && stepIdx === 0 && (
          <Box sx={{ maxWidth: 460, mx: 'auto', pt: { sm: 4 } }}>
            <Typography variant="h6" fontWeight={700}>Who&apos;s applying?</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 3 }}>
              Enter the applicant&apos;s CNIC or B-Form number. We&apos;ll check whether they&apos;re
              already in the school&apos;s records.
            </Typography>
            <TextField
              label="CNIC / B-Form number"
              placeholder="35202-1234567-1"
              value={formatCnic(cnic)}
              onChange={(e) => setCnic(cnicDigits(e.target.value))}
              onKeyDown={(e) => { if (e.key === 'Enter' && identified) goToDetails(); }}
              inputRef={cnicInputRef}
              inputProps={{ inputMode: 'numeric', maxLength: 15 }}
              InputProps={{ sx: { fontFamily: 'monospace', fontSize: '1.15rem', letterSpacing: 1 } }}
              helperText="13 digits — the same number carries from B-Form to CNIC"
              fullWidth
            />
            <Box sx={{ mt: 2.5 }}>
              <LookupResult lookup={lookup} />
            </Box>
            {lookup.state === 'new' && (
              <>
                <TextField
                  label="Father's CNIC"
                  placeholder="35202-1234567-1"
                  value={formatCnic(fatherCnic)}
                  onChange={(e) => setFatherCnic(cnicDigits(e.target.value))}
                  onKeyDown={(e) => { if (e.key === 'Enter' && identified) goToDetails(); }}
                  inputProps={{ inputMode: 'numeric', maxLength: 15 }}
                  InputProps={{ sx: { fontFamily: 'monospace', fontSize: '1.15rem', letterSpacing: 1 } }}
                  helperText="We'll check whether he's already a parent at the school — a brother or sister here keeps the family together"
                  fullWidth
                  autoFocus
                  sx={{ mt: 3 }}
                />
                <Box sx={{ mt: 2.5 }}>
                  <FatherLookupResult lookup={fatherLookup} />
                </Box>
              </>
            )}
            {openInvite && (
              <Box sx={{ mt: 2.5 }}>
                <OpenInviteNotice invite={openInvite} emailOn={emailOn} onResent={setOpenInvite} />
              </Box>
            )}
          </Box>
        )}

        {path === 'fill' && stepIdx === 1 && (
          <Stack spacing={2.5}>
            {lookup.state === 'returning' && (
              <Typography variant="body2" color="text.secondary">
                {lookup.grNumber ? 'Returning student' : 'Applied before'} — family and guardian details are
                already on file, so only the applicant&apos;s details are needed.
              </Typography>
            )}
            {joining && (
              <Typography variant="body2" color="text.secondary">
                Joining the {joining.family.displayName || 'existing'} family. The parents&apos; details are filled in from the
                family record — correct anything that has changed and it will be updated there too.
                {motherOnRecord ? '' : ' Add the mother if she isn\'t recorded yet.'}
              </Typography>
            )}
            <Typography variant="caption" color="text.secondary">
              Fields marked * are required.
            </Typography>
            <DynamicFieldRenderer
              sections={sections}
              values={values}
              errors={errors}
              touched={touched}
              onChange={(k, v) => setValues(prev => ({ ...prev, [k]: v }))}
              onBlur={(k) => setTouched(prev => ({ ...prev, [k]: true }))}
              classOptions={openClassOptions}
              fieldHints={dobHint}
              cardSections={false}
              disabled={submitting}
            />
            {checklist.length > 0 && (
              <Box>
                <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}>
                  Documents
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
                  Attach what the family has brought — anything else can be added later on the Documents tab.
                </Typography>
                <DocumentChecklistInput
                  items={checklist}
                  picked={docFiles}
                  onPick={(key, file) => setDocFiles(prev => ({ ...prev, [key]: file }))}
                  errors={docErrors}
                  disabled={submitting}
                />
              </Box>
            )}
          </Stack>
        )}

        {path === 'fill' && stepIdx === 2 && (
          <Stack spacing={2}>
            <ReviewCard title="Identity">
              <ReviewItem label="CNIC / B-Form" value={formatCnic(cnic)} mono />
              <ReviewItem
                label="Record"
                value={lookup.state === 'returning' ? returningTitle(lookup) : 'New applicant'}
              />
            </ReviewCard>
            {lookup.state === 'new' && (
              <ReviewCard title="Family" onEdit={() => setStepIdx(0)}>
                <ReviewItem label="Father's CNIC" value={formatCnic(fatherCnic)} mono />
                <ReviewItem
                  label="Family"
                  value={joining
                    ? `Joins the ${joining.family.displayName || 'existing'} family · siblings: ${joining.children.map(c => `${c.name} (${grLabel(c)})`).join(', ')}`
                    : 'New family'}
                  full
                />
                {joining && (() => {
                  // What will be corrected on the family record.
                  const changes = (['father', 'mother'] as const).flatMap(rel => {
                    const g = joining.guardians.find(x => x.relation === rel);
                    if (!g) return [];
                    return (['Name', 'Phone', 'Email', 'Occupation'] as const)
                      .filter(f => {
                        const v = String(values[`${rel}${f}`] ?? '').trim();
                        return v && v !== String((g as any)[f.toLowerCase()] ?? '').trim();
                      })
                      .map(f => `${rel}'s ${f.toLowerCase()}`);
                  });
                  return (
                    <ReviewItem
                      label="Family record"
                      value={changes.length ? `Will be updated: ${changes.join(', ')}` : 'No changes to the parents\' details'}
                      full
                    />
                  );
                })()}
              </ReviewCard>
            )}
            {sections.map(section => {
              const answered = section.fields
                .map(f => ({ f, text: fieldDisplayValue(f, values[f.key], classOptions) }))
                .filter(x => x.text);
              if (answered.length === 0) return null;
              return (
                <ReviewCard key={section.key} title={section.title} onEdit={() => setStepIdx(1)}>
                  {answered.map(({ f, text }) => (
                    <ReviewItem
                      key={f.key}
                      label={f.label}
                      value={text}
                      full={f.type === 'textarea'}
                    />
                  ))}
                </ReviewCard>
              );
            })}
            {checklist.length > 0 && (
              <ReviewCard title="Documents" onEdit={() => setStepIdx(1)}>
                {checklist.map(item => (
                  <ReviewItem key={item.key} label={item.label} value={docFiles[item.key]?.name ?? (item.required ? 'To be added later (required before enrollment)' : 'Not attached')} />
                ))}
              </ReviewCard>
            )}
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.05),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              }}
            >
              <Typography variant="body2" fontWeight={600}>What happens next</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                {fee
                  ? `The application is created as “App fee pending” and an application fee invoice of ${fee} is raised.`
                  : 'The application is created and moves straight to “Under review”.'}
              </Typography>
            </Box>
          </Stack>
        )}
    </StepDialog>
  );
};

const Checking: React.FC = () => (
  <Stack direction="row" spacing={1.25} alignItems="center" sx={{ color: 'text.secondary' }}>
    <CircularProgress size={16} thickness={5} />
    <Typography variant="body2">Checking the school&apos;s records…</Typography>
  </Stack>
);

/** A former student by GR number; someone who only applied before has none. */
const returningTitle = (l: { grNumber?: string }) => (l.grNumber ? `Returning student · GR ${l.grNumber}` : 'Applied before');

/** Inline result of the applicant CNIC check under the step-1 field. */
const LookupResult: React.FC<{ lookup: Lookup }> = ({ lookup }) => {
  const theme = useTheme();
  if (lookup.state === 'idle') return null;
  if (lookup.state === 'checking') return <Checking />;
  return (
    <ResultCard
      {...(lookup.state === 'new'
        ? { tone: theme.palette.success, Icon: CheckCircleOutline, title: 'New applicant', body: 'No existing record — a new profile will be created.' }
        : lookup.state === 'returning'
          ? {
            tone: theme.palette.info, Icon: History, title: returningTitle(lookup),
            body: lookup.grNumber
              ? `${lookup.name} is already on file. Their GR number and family are kept.`
              : `${lookup.name} is on file from an earlier application. Their record and family are kept.`,
          }
          : { tone: theme.palette.error, Icon: ErrorOutline, title: 'Couldn’t check records', body: lookup.message })}
    />
  );
};

/** Result of the father CNIC check — a new family, or the family a sibling is already in. */
const FatherLookupResult: React.FC<{ lookup: FatherLookup }> = ({ lookup }) => {
  const theme = useTheme();
  if (lookup.state === 'idle') return null;
  if (lookup.state === 'checking') return <Checking />;
  if (lookup.state === 'error') return <ResultCard tone={theme.palette.error} Icon={ErrorOutline} title="Couldn’t check the father’s CNIC" body={lookup.message} />;
  if (lookup.state === 'new') {
    return <ResultCard tone={theme.palette.success} Icon={CheckCircleOutline} title="New family" body="This father isn't on record yet — a new family will be created with the parents' details you enter next." />;
  }
  const { family, children, guardians } = lookup.match;
  const father = guardians.find(g => g.relation === 'father');
  return (
    <ResultCard
      tone={theme.palette.info}
      Icon={FamilyRestroomOutlined}
      title={`${father?.name ?? 'This parent'} is already a parent here`}
      body={`The child joins the ${family.displayName || 'existing'} family. Siblings: ${children.map(c => `${c.name} (${grLabel(c)})`).join(', ') || 'none yet'}. The parents' details are taken from the record.`}
    />
  );
};

const ResultCard: React.FC<{ tone: { main: string }; Icon: React.ElementType; title: string; body: string }> = ({ tone, Icon, title, body }) => {
  const view = { tone, Icon, title, body };
  return (
    <Stack
      direction="row"
      spacing={1.5}
      sx={{
        p: 1.75,
        borderRadius: 2,
        bgcolor: alpha(view.tone.main, 0.06),
        border: `1px solid ${alpha(view.tone.main, 0.25)}`,
      }}
    >
      <view.Icon sx={{ color: view.tone.main, fontSize: 20, mt: 0.1 }} />
      <Box>
        <Typography variant="body2" fontWeight={600}>{view.title}</Typography>
        <Typography variant="body2" color="text.secondary">{view.body}</Typography>
      </Box>
    </Stack>
  );
};

const ReviewCard: React.FC<{ title: string; onEdit?: () => void; children: React.ReactNode }> = ({ title, onEdit, children }) => (
  <Paper variant="outlined" sx={{ borderRadius: 2, px: 2.25, py: 1.75 }}>
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
      <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.6 }}>
        {title}
      </Typography>
      {onEdit && (
        <Button size="small" onClick={onEdit} sx={{ textTransform: 'none', minWidth: 0, py: 0 }}>Edit</Button>
      )}
    </Stack>
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 3, rowGap: 1.25 }}>
      {children}
    </Box>
  </Paper>
);

const ReviewItem: React.FC<{ label: string; value: string; mono?: boolean; full?: boolean }> = ({ label, value, mono, full }) => (
  <Box sx={{ gridColumn: full ? { sm: 'span 2' } : undefined, minWidth: 0 }}>
    <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
    <Typography variant="body2" fontWeight={500} sx={{ fontFamily: mono ? 'monospace' : undefined, wordBreak: 'break-word' }}>
      {value}
    </Typography>
  </Box>
);

export default WalkInApplicationDialog;
